import './favorites.css';

import { EventEmitter } from "eventemitter3";
import { Router } from '../../../core/router'
import { ProfileService } from '../../../services/profile-service';
import { OrderService } from '../../../services/order-service';
import { DataLayerService } from '../../../services/data-layer-service';
import { Data } from "../../../models/data";
import { Page } from "../page";
import { Modals, OrderType, Pages, ContentType } from "../../../types/enums";
import { Util } from "../../../utils/util";
import { ITabBarData } from "../../shared/TabBar/tab-bar.interface";
import { TabBar } from "../../shared/TabBar/tab-bar";
import { EventTypes } from '../../../core/event-bus';
import { IName } from '../../../interfaces/global.interfaces';
import { DialogCreators } from '../../../utils/dialog-creators';
import { IUserFavoriteItem, IUserFavoriteItemOrderItemModifier, IUserSpecialInstructions } from '../../../models/user.interface';
import { IDataInstruction, IDataItem, IDataPreModifier, IItemModifierDisplayGroupModifier, IStoreLocation, IStoreLocationHour } from '../../../models/data.interface';
import { Dialog } from '../../modals/Dialog/dialog';
import { IOrderItem, IOrderItemModifier } from '../../../models/order.interface';
import { TimedMessage } from '../../shared/TimedMessage/timed-message';
import { ICustomizeItemProps } from '../../modals/CustomizeItem/customize-item.interface';
import { Context } from '../../../models/context';
import { LoaderSpinner } from '../../shared/LoaderSpinner/loader-spinner';
import { LocalizationService } from '../../../services/localization-service';

export class Favorites extends Page {
    private readonly _profileService: ProfileService;
    private readonly _orderService: OrderService;
    private readonly _dataLayer: DataLayerService;

    private _dataLayerObserverSet: boolean = false;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, profileService: ProfileService, orderService: OrderService,
        dataLayer: DataLayerService) {
        super("page_favorites", eventBus, data, context, localizationService);
        this._profileService = profileService;
        this._orderService = orderService;
        this._dataLayer = dataLayer;
        this._eventBusListeners();
    }

    public async initAsync(): Promise<void> {
        await this._renderPageAsync();
    }

    public init(): void {
        super.init();
        this._buildFavoritesTabBar();
        this._onTabButtonClick(-1);
    }

    public override show(): void {
        super.show();

        if (!this._profileService.user.isLoggedIn()) {
            this._renderNotLoggedIn();
        } else {
            this._buildFavoritesPageAsync();
        }

        if (this._dataLayer)
            this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.storeKey ? this._data.getProperty("StoreInfo").storeCode.toString() : '0', this._profileService.user.user);

        this._context.setProperty("upsellSelected", false);
    }

    private readonly _renderPageAsync = async (): Promise<void> => {
        if (this._profileService.user.isLoggedIn()) {
            await this._initFavorites();
        }
    };

    private readonly _eventBusListeners = (): void => {
        this._eventBus.on(EventTypes.LOGGED_IN, Util.Debounce(async () => {
            await this._initFavorites();
        }, 1000, true));
        this._eventBus.on(EventTypes.LOGGED_OUT, this._renderNotLoggedIn);
        this._eventBus.on(EventTypes.FAV_ITEM_SAVE, async (favItem: IUserFavoriteItem) => { await this._appendNewFavoriteItemUI(favItem); });
        this._eventBus.on(EventTypes.FAV_ITEM_DELETE, async (favItemId: number) => { await this._removeFavoriteItemUI(favItemId); });
        this._eventBus.on(EventTypes.FAV_STORE_ADD, async (storeKey: string) => { await this._appendNewFavoriteStoreUI(storeKey); });
        this._eventBus.on(EventTypes.FAV_STORE_REMOVE, async (payload: { storeKey: string; isRemoved: boolean }) => { await this._removeFavoriteStoreUI(payload.storeKey); });
        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, async () => { await this._buildFavoritesPageAsync(); });
        this._eventBus.on(EventTypes.DATALAYER_LOADED, () => {
            //initial attempt to capture page_view may not have had the dataLayer loaded yet
            if (this._element?.classList.contains("open"))
                this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.storeKey ? this._data.getProperty("StoreInfo").storeCode.toString() : '0', this._profileService.user.user);
        });
    };

    private readonly _initFavorites = async (): Promise<void> => {
        await this._fetchAndSetFavoriteMenuItems();
        await this._fetchAndSetFavoriteStores();
        await this._buildFavoritesPageAsync();
    }

    private readonly _buildFavoritesPageAsync = async (): Promise<void> => {
        await this._buildFavoriteItemsList();
        await this._buildFavoriteStoresList();
        if (Router.path === Pages.FAVORITES)
            this._setupDataLayerObservers();
    };

    /**
     * This method sets up observers for favorited menu items, allowing the Data Layer to track a user viewing these elements.
     */
    private _setupDataLayerObservers(): void {
        if (this._dataLayer && !this._dataLayerObserverSet) {
            const favoritesDiv = document.getElementById("favorite-items-container") as HTMLElement;
            if (favoritesDiv) {
                this._dataLayer.setupItemsObserver(favoritesDiv, '[name="div_favorite_items_item"]', 'favorites', {
                    datasetKey: 'id',
                    handler: this._dataLayer.view_item_list
                });

                this._dataLayerObserverSet = true;
            }
        }
    }

    /**
     * Fetches and sets favorite menu items in the user profile.
     */
    private readonly _fetchAndSetFavoriteMenuItems = async (): Promise<void> => {
        const favoriteMenuItems = await this._profileService.getFavoriteItems();
        this._profileService.user.setProperty("FavoriteItems", favoriteMenuItems);
    }

    /**
     * Fetches and sets favorite stores in the user profile.
     */
    private readonly _fetchAndSetFavoriteStores = async (): Promise<void> => {
        const favoriteStores = await this._profileService.fetchFavoriteStores();
        const favoriteStoreKeys = favoriteStores.map(store => store.storeKey);
        this._profileService.user.setProperty("FavoriteStores", favoriteStoreKeys);
    };

    /**
     * Handles event where favorite item was newly saved, rerenders favorite items.
     */
    private readonly _appendNewFavoriteItemUI = async (favItem: IUserFavoriteItem): Promise<void> => {
        const favoriteItems = this._profileService.user.getProperty("FavoriteItems") || [];

        if (favoriteItems.findIndex(item => item.favoriteItemId === favItem.favoriteItemId) === -1) {
            this._profileService.user.setProperty("FavoriteItems", [...favoriteItems, favItem]);
        }

        await this._buildFavoriteItemsList();
    }

    /**
     * Handles event where favorite item was removed, rerenders favorite items.
     */
    private readonly _removeFavoriteItemUI = async (favItemId: number): Promise<void> => {
        const favoriteItems = this._profileService.user.getProperty("FavoriteItems") || [];

        this._profileService.user.setProperty("FavoriteItems", favoriteItems.filter(item => item.favoriteItemId !== favItemId));

        await this._buildFavoriteItemsList();
    }

    /**
     * Handles event where favorite store was newly saved, rerenders favorite stores.
     */
    private readonly _appendNewFavoriteStoreUI = async (storeKey: string): Promise<void> => {
        const favoriteStores = this._profileService.user.getProperty("FavoriteStores") || [];
        if (favoriteStores.indexOf(storeKey) === -1) {
            this._profileService.user.setProperty("FavoriteStores", [...favoriteStores, storeKey]);
        }

        await this._buildFavoriteStoresList();
    };

    /**
     * Handles event where favorite store was removed, rerenders favorite stores.
     */
    private readonly _removeFavoriteStoreUI = async (storeKey: string): Promise<void> => {
        const favoriteStores = this._profileService.user.getProperty("FavoriteStores") || [];
        this._profileService.user.setProperty("FavoriteStores", favoriteStores.filter(key => key !== storeKey));

        await this._buildFavoriteStoresList();
    };

    private readonly _buildFavoriteItemsList = async (): Promise<void> => {
        Util.setElement("innerHTML", "div_favorite_items_parent", "");

        const store = this._data.storeKey;
        if (!store) {
            this._renderNoStoreSelected();
            return;
        }

        const userFavItems = this._profileService.user.getProperty("FavoriteItems") || [];

        if (userFavItems.length > 0) {
            const cards = await Promise.all(userFavItems.map(async (favItem, index) => {
                return await this._createFavoriteItemCard(favItem, index);
            }));

            const containerDiv = document.createElement("div");
            containerDiv.classList.add("favorite-container");
            containerDiv.id = "favorite-items-container";
            cards.filter(Boolean).forEach(card => containerDiv.appendChild(card!));

            document.getElementById("div_favorite_items_parent")?.appendChild(containerDiv);

            if (cards.length === 0) this._renderNoFavMenuItem();
        } else {
            this._renderNoFavMenuItem();
        }
    }

    private readonly _createFavoriteItemCard = async (favItem: IUserFavoriteItem, cardIndex: number): Promise<HTMLElement | null> => {
        if (!this._isValidFavoriteItem(favItem)) return null;

        const isMultiItem = this._isMultiItem(favItem);

        const dataItems = this._data.getProperty("Items");
        const firstDataItem = dataItems.find(x => x.ItemId === favItem.items[0].itemId);
        const secondDataItem = isMultiItem ? dataItems.find(x => x.ItemId === favItem.items[1].itemId) : null;

        const dataSize = firstDataItem?.SizeCalories?.find(s => s.SizeId === favItem.items[0].sizeId);
        
        // Determine if this is an available or unavailable item
        const isAvailable = !!firstDataItem && !!dataSize && (isMultiItem ? !!secondDataItem : true);
        
        // Use fallback data if item not in current menu
        const firstImageUrl = firstDataItem?.ImageURL ?? favItem.items[0].imageUrl;
        const secondImageUrl = secondDataItem?.ImageURL ?? (isMultiItem ? favItem.items[1].imageUrl : null);
        const images = [firstImageUrl, secondImageUrl];

        const names = firstDataItem?.Names ?? favItem.items[0].names;

        const card = this._createCardContainer();
        card.setAttribute('data-id', favItem.items[0].itemId.toString());

        const imageSection = this._createImageSection(favItem, images, names, isAvailable);
        const detailsSection = this._createDetailsSection(favItem, names);

        card.appendChild(imageSection);
        card.appendChild(detailsSection);

        if (isAvailable) {
            card.appendChild(this._createFavoriteItemButtons(favItem, firstDataItem, cardIndex));
        } else {
            this._setCardAsUnavailable(card);
            card.appendChild(this._createUnavailableFavoriteItemButtons());
        }

        return card;
    };

    private _isValidFavoriteItem(favItem: IUserFavoriteItem): boolean {
        return !!(favItem.items?.length && favItem.items[0]);
    }

    private _createCardContainer(): HTMLDivElement {
        const card = document.createElement("div");
        card.setAttribute('name', 'div_favorite_items_item');
        card.classList.add("favorite-item-card");
        return card;
    }

    private _isMultiItem(favItem: IUserFavoriteItem): boolean {
        return favItem.items.length > 1 && favItem.items[0].itemId !== favItem.items[1].itemId;
    }

    private _setCardAsUnavailable(card: HTMLDivElement): void {
        if (!card) return;

        const titleEl = card.querySelector<HTMLElement>(".favorite-item-name");
        const itemName = titleEl?.textContent?.trim() ?? "Item";

        const accessibleLabel = `${itemName}. ${this._localizationService.names("FavoriteItemUnavailable")}`;

        // Make card a single accessible element
        card.setAttribute("tabindex", "0");
        card.setAttribute("aria-label", accessibleLabel);
        card.setAttribute("aria-disabled", "true");
        card.classList.add("favorite-item-card--unavailable");

        // Hide all children from the screen reader EXCEPT the card container itself for ADA: �{Item Name}. Item unavailable at this location.�
        Array.from(card.children).forEach(child => {
            child.setAttribute("aria-hidden", "true");
        });

        // Keep overlay visible visually
        const overlay = document.createElement("div");
        overlay.classList.add("favorite-item-card__overlay");
        overlay.setAttribute("aria-hidden", "true");

        const overlayText = document.createElement("span");
        overlayText.classList.add("favorite-item-card__overlay-text");
        overlayText.textContent = this._localizationService.names("FavoriteItemUnavailable");

        overlay.appendChild(overlayText);
        card.appendChild(overlay);
    }

    private _createImageSection(favItem: IUserFavoriteItem, imageUrls: (string | null)[], names: IName[], isAvailable: boolean): HTMLElement {
        const isMultiItem = this._isMultiItem(favItem);

        const imageWrapper = document.createElement("div");
        imageWrapper.classList.add("favorite-item-image-wrapper");
        if (isMultiItem)
            imageWrapper.classList.add("half-half");

        for (const imageUrl of imageUrls) {
            if (!imageUrl)
                continue;

            const image = document.createElement("img");
            image.src = imageUrl;
            image.alt = favItem.favoriteItemName || this._localizationService.GetName(names);
            image.classList.add("favorite-item-image");
            imageWrapper.appendChild(image);
        }

        const favoriteIconButton = this._createFavoriteItemIcon(favItem.favoriteItemId, !isAvailable);

        imageWrapper.appendChild(favoriteIconButton);

        return imageWrapper;
    }

    private _createDetailsSection(favItem: IUserFavoriteItem, names: IName[]): HTMLElement {
        const dataItems = this._data.getProperty("Items");
        const dataPreMods = this._data.getProperty("PreModifiers");
        const dataInstructions = this._data.getProperty("Instructions");

        const isMultiItem = this._isMultiItem(favItem);

        const details = document.createElement("div");
        details.classList.add("favorite-item-details");

        const itemNameText = favItem.favoriteItemName || (isMultiItem ? this._localizationService.names("HHP") : this._localizationService.GetName(names));

        const itemName = document.createElement("h3");
        itemName.textContent = itemNameText;
        itemName.classList.add("favorite-item-name");
        details.appendChild(itemName);

        const firstDataItem = dataItems.find(x => x.ItemId === favItem.items[0].itemId);
        const secondDataItem = isMultiItem ? dataItems.find(x => x.ItemId === favItem.items[1].itemId) : null;

        const leftItemName = isMultiItem && firstDataItem ? this._localizationService.GetName(firstDataItem.Names) : "";
        const rightItemName = isMultiItem && secondDataItem ? this._localizationService.GetName(secondDataItem.Names) : "";

        const sizeId = favItem.items[0]?.sizeId ?? null;
        let sizeName: IName[] = [];
        if (sizeId && sizeId > 0) {
            const size = this._data.getProperty("Sizes").find(x => x.SizeId === sizeId) ?? null;
            if (size)
                sizeName = size.Names;
        }

        const modifierData = this._getModifiersBySide(favItem, dataItems, dataPreMods);
        this._renderSideModifiers(modifierData.leftMods, "Left", details, leftItemName);
        this._renderSideModifiers(modifierData.rightMods, "Right", details, rightItemName);
        this._renderWholeModifiers(modifierData.wholeMods, details, sizeName);
        this._renderSpecialInstructions(favItem.items[0]?.instructions ?? [], dataInstructions, details);

        if (modifierData.leftMissing || modifierData.rightMissing) {
            this._renderMissingModsWarning(details);
        }

        const itemPrice = document.createElement("p");

        let price = favItem?.items[0].price;
        if (favItem?.items[1]) price += favItem?.items[1].price;

        itemPrice.textContent = `$${price.toFixed(2)}`;
        itemPrice.classList.add("favorite-item-price");

        details.appendChild(itemPrice);

        return details;
    }

    private _getModifiersBySide(
        favItem: IUserFavoriteItem,
        dataItems: IDataItem[],
        dataPreMods: IDataPreModifier[]
    ): {
        leftMods: Map<string, boolean>,
        rightMods: Map<string, boolean>,
        wholeMods: string[],
        leftMissing: boolean,
        rightMissing: boolean
    } {
        const left = this._collectModifiersForSide(favItem, 1, dataItems, dataPreMods);
        const right = this._collectModifiersForSide(favItem, 2, dataItems, dataPreMods);

        const { wholeMods, leftMods, rightMods } = this._extractWholeModifiers(left.mods, right.mods);

        return {
            leftMods,
            rightMods,
            wholeMods,
            leftMissing: left.missing,
            rightMissing: right.missing
        };
    }

    private _collectModifiersForSide(
        favItem: IUserFavoriteItem,
        halfIndex: 1 | 2,
        dataItems: IDataItem[],
        dataPreMods: IDataPreModifier[]
    ): { mods: Map<string, boolean>, missing: boolean } {
        const mods = new Map<string, boolean>();
        let missing = false;

        const items = favItem.items.filter(i => i.halfIndex === halfIndex);

        for (const item of items) {
            const matchedItem = dataItems.find(x => x.ItemId === item.itemId);
            if (!matchedItem) continue;

            const [modsText, isMissing] = this._getModifiersText(item.modifiers, matchedItem, dataPreMods);
            if (!modsText.trim()) continue;

            modsText.split(",").map(s => s.trim()).forEach(mod => {
                mods.set(mod, isMissing);
            });

            missing ||= isMissing;
        }

        return { mods, missing };
    }

    private _extractWholeModifiers(
        leftMods: Map<string, boolean>,
        rightMods: Map<string, boolean>
    ): {
        wholeMods: string[],
        leftMods: Map<string, boolean>,
        rightMods: Map<string, boolean>
    } {
        const wholeMods: string[] = [];

        for (const mod of Array.from(leftMods.keys())) {
            if (rightMods.has(mod)) {
                wholeMods.push(mod);
                leftMods.delete(mod);
                rightMods.delete(mod);
            }
        }

        return { wholeMods, leftMods, rightMods };
    }

    private _renderSideModifiers(mods: Map<string, boolean>, label: string, container: HTMLElement, itemName: string): void {
        if (mods.size === 0) return;

        const itemNameText = itemName ? `${itemName}, ` : "";

        const modLine = document.createElement("p");
        modLine.classList.add("favorite-item-description");
        modLine.innerHTML = `${label}: ${itemNameText}${Array.from(mods.keys()).join(", ").replace(/~~(.*?)~~/g, "<s>$1</s>")}`;
        container.appendChild(modLine);
    }

    private _renderWholeModifiers(mods: string[], container: HTMLElement, sizeName: IName[]): void {
        if (!mods.length && !sizeName.length) return;

        const modsList = mods.map(mod => `<li>${mod.replaceAll(/~~(.*?)~~/g, "<s>$1</s>")}</li>`);
        const size = sizeName.length ? `<li>${this._localizationService.GetName(sizeName)}</li>` : "";

        const html = `
            <section class="favorite-item-description">
                <span>${this._localizationService.names("Whole")}:</span>
                <ul class="list">
                    ${size}
                    ${modsList.join("")}
                </ul>
            </section>`;

        container.appendChild(Util.createHtmlElementFromTemplate(html));
    }

    private _renderSpecialInstructions(instructions: IUserSpecialInstructions[], dataInstructions: IDataInstruction[], container: HTMLElement): void {
        if (!instructions.length)
            return;

        const instructionTexts = dataInstructions
            .filter(inst => instructions.some(instruction => instruction.specialInstructionId === inst.SpecialInstructionsId && instruction.commentId === inst.CommentId))
            .map(inst => `<li>${this._localizationService.GetNewName(inst.Names)}</li>`);

        const html = `
            <section class="favorite-instructions">
                <span>${this._localizationService.names("SPECIAL_INSTRUCTIONS")}:</span>
                <ul class="list">
                    ${instructionTexts.join("")}
                </ul>
            </section>`;

        container.appendChild(Util.createHtmlElementFromTemplate(html));
    }

    private _renderMissingModsWarning(container: HTMLElement): void {
        const warn = document.createElement("p");
        warn.textContent = this._localizationService.names("FavoriteItemMissingMod");
        warn.classList.add("missing-mods-message");
        container.appendChild(warn);
    }

    private readonly _getModifiersText = (
        userMods: IUserFavoriteItemOrderItemModifier[],
        dataItem: IDataItem,
        dataPreMods: IDataPreModifier[]
    ): [string, boolean] => {

        const modifierTextList: string[] = [];
        let missingMods: boolean = false;

        // Collect all modifiers from display groups
        const allModifiers: IItemModifierDisplayGroupModifier[] = [];
        dataItem.ModifierDisplayGroups.forEach(group => {
            allModifiers.push(...group.Modifiers);
        });

        // Get all default modifiers
        const defaultModifiers = allModifiers.filter(m => m.IsDefault);

        const defaultPremodifierId = this._data.getProperty("DefaultPreModifierId");

        // prepend "No" to unselected default modifiers #56806
        defaultModifiers.forEach((defaultMod) => {
            const existsInUserMods = userMods.some(mod => mod.modifierId === defaultMod.ModifierId);
            if (!existsInUserMods) {
                const parsedJson: IName[] = JSON.parse(defaultMod.Name);
                const defaultModName = this._localizationService.GetName(parsedJson);
                modifierTextList.push(`${this._localizationService.names("No")} ${defaultModName}`);
            }
        });

        // Process user-selected modifiers, BUG 54791 remove check for ishidden
        for (const mod of userMods) {
            const modData = allModifiers.find(m => m.ModifierId === mod.modifierId);
            if (!modData) {
                missingMods = true;
                continue;
            }

            //check if current user mod is default and is in default premod, if true continue
            if (defaultPremodifierId == mod.preModifierId && modData.IsDefault) {
                continue;
            }

            const parsedJson: IName[] = JSON.parse(modData.Name);
            const modName = this._localizationService.GetName(parsedJson).trimEnd();

            const preModData = dataPreMods.find(pm => pm.PreModifierId === mod.preModifierId);
            const preModName = preModData ? this._localizationService.GetName(preModData.Names).trimEnd() : "";

            const finalModText = preModName ? `${preModName} ${modName}` : modName;
            modifierTextList.push(finalModText);
        };

        return [modifierTextList.length > 0 ? modifierTextList.join(", ") : "", missingMods];
    };


    private readonly _createFavoriteItemIcon = (favItemId: number, isDisabled: boolean = false): HTMLElement => {
        const favoriteIconButton = document.createElement("button");
        favoriteIconButton.id = "favorite-item-icon";
        favoriteIconButton.setAttribute("aria-label", isDisabled ? "favorite unavailable" : "remove favorite");
        favoriteIconButton.classList.add("icon-heart-filled", "favorites");
        favoriteIconButton.disabled = isDisabled;
        
        if (!isDisabled) {
            favoriteIconButton.onclick = async () => {
                this._showConfirmRemoveFavoriteItem(favItemId);
            };
        }

        return favoriteIconButton;
    };

    private readonly _showConfirmRemoveFavoriteItem = (favItemId: number): void => {
        new Dialog(
            this._localizationService.names("FavoriteItem_Delete_Confirm"),
            [
                { label: this._localizationService.names("Cancel").toUpperCase(), value: "no", isSecondary: true },
                { label: this._localizationService.names("Remove").toUpperCase(), value: "yes", action: () => this._handleRemoveFavoriteItem(favItemId) }
            ]);
    };

    private readonly _handleRemoveFavoriteItem = async (favItemId: number): Promise<void> => {
        const loader = new LoaderSpinner();
        const favItemName = this._profileService.user.getProperty("FavoriteItems")?.find(x => x.favoriteItemId == favItemId)?.favoriteItemName ?? '';
        const checkDelete = await this._profileService.deleteFavoriteItem(favItemId);

        if (checkDelete)
            this._eventBus.emit(EventTypes.FAV_ITEM_DELETE, favItemId);
        else
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("FavoriteItem_Delete_Error"),
                true
            );

        loader.destroy();

        if (this._dataLayer)
            this._dataLayer.remove_favorite(ContentType.MENU_ITEM, favItemName);
    }

    private readonly _createFavoriteItemButtons = (favItem: IUserFavoriteItem, dItem: IDataItem, itemIndex: number): HTMLElement => {
        const buttonContainer = document.createElement("div");
        buttonContainer.classList.add("favorite-item-buttons");

        // Customize button
        const customizeButton = document.createElement("button");
        customizeButton.textContent = this._localizationService.names("Customize").toUpperCase();
        customizeButton.classList.add("customize-button", "secondary");

        customizeButton.addEventListener("click", () => {
            const orderTypeId = this._orderService.order.getProperty("OrderTypeID");
            const defaultSizeId = this._data.getProperty("Settings")?.DSZID;

            if (!(orderTypeId && defaultSizeId)) return;

            const oItems = this._retrieveOrderItems(dItem, favItem);

            if (this._dataLayer) {
                const orderItem = this._orderService.order._createOrderItem(dItem, defaultSizeId, 0, 1, []);
                this._dataLayer.select_item(this._orderService.order, orderItem, itemIndex, Pages.FAVORITES.replace('/', ''));
            }

            const props: ICustomizeItemProps = {
                sizeId: oItems[0].SizeId, //get first item's sizeId because all h/h should be same size
                quantity: favItem.items[0].quantity,
                mealDeal: null,
                recentOrder: false,
                mItem: dItem,
                itemsToEdit: oItems
            };

            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.CUSTOMIZE_ITEM, props);
        });
        buttonContainer.appendChild(customizeButton);

        const addToOrderButton = document.createElement("button");
        addToOrderButton.textContent = this._localizationService.names("AddToOrder").toUpperCase();
        addToOrderButton.classList.add("add-to-order-button");
        addToOrderButton.addEventListener("click", async () => { await this._handleAddToOrderClick(favItem, dItem); });
        buttonContainer.appendChild(addToOrderButton);

        return buttonContainer;
    };

    private _createUnavailableFavoriteItemButtons(): HTMLElement {
        const buttonContainer = document.createElement("div");
        buttonContainer.classList.add("favorite-item-buttons");

        // Customize button
        const customizeButton = document.createElement("button");
        customizeButton.textContent = this._localizationService.names("Customize").toUpperCase();
        customizeButton.classList.add("customize-button", "secondary");
        customizeButton.disabled = true;

        // Hide from screen readers
        customizeButton.setAttribute("aria-hidden", "true");
        customizeButton.setAttribute("tabindex", "-1");
        customizeButton.setAttribute("role", "presentation");

        buttonContainer.appendChild(customizeButton);

        // Add to Order button
        const addToOrderButton = document.createElement("button");
        addToOrderButton.textContent = this._localizationService.names("AddToOrder").toUpperCase();
        addToOrderButton.classList.add("add-to-order-button");
        addToOrderButton.disabled = true;

        // Hide from screen readers
        addToOrderButton.setAttribute("aria-hidden", "true");
        addToOrderButton.setAttribute("tabindex", "-1");
        addToOrderButton.setAttribute("role", "presentation");

        buttonContainer.appendChild(addToOrderButton);

        return buttonContainer;
    }

    /**
     * Handles the click event for adding a favorite item to the order.
     * @param {IUserFavoriteItem} favItem - The favorite item to be added.
     * @param {IDataItem} dItem - The data item associated with the favorite item.
     */
    private async _handleAddToOrderClick(favItem: IUserFavoriteItem, dItem: IDataItem): Promise<void> {
        const oItems = this._retrieveOrderItems(dItem, favItem);
        await this._orderService.order.addItems(this._profileService.loyaltyProvider, oItems);
        const addedToCartMsg = favItem.items[0].quantity > 0 ?
            this._localizationService.names("ItemsAddedToCart_Plural").replace("{{number}}", favItem.items[0].quantity.toString()) : this._localizationService.names("ItemsAddedToCart_Single");
        const msg = new TimedMessage(addedToCartMsg, this._localizationService.names("ProceedToCartToCheckout"), 1000);
        msg.show();

        if (this._dataLayer) {
            this._dataLayer.add_to_cart(this._orderService.order, oItems, Router.path.replace('/', ''));
        }
    }

    private _retrieveOrderItems(dItem: IDataItem, favItem: IUserFavoriteItem): IOrderItem[] {
        const oItems: IOrderItem[] = [];
        const nextId = this._orderService.order.getNextItemKey(); //on add to order/customize, its always new item
        let nextRecId = this._orderService.order.getItemRecordId();

        for (const item of favItem.items) {
            const oItemModifier = this.favoriteItemModifiersToOrderModifiers(item.modifiers);
            const oItem = this._orderService.order._createOrderItem(dItem, item.sizeId, item.halfIndex, item.quantity, oItemModifier);
            oItem.ItemKey = nextId;
            oItem.ItemRecId = nextRecId;
            oItems.push(oItem);

            nextRecId++;
        }

        return oItems;
    }

    private favoriteItemModifiersToOrderModifiers = (favItemMods: IUserFavoriteItemOrderItemModifier[]): IOrderItemModifier[] => {
        const orderItemModifiers: IOrderItemModifier[] = [];
        for (const itemModifier of favItemMods) {
            const modifier = this._data.getProperty("Modifiers").find(m => m.ModifierId === itemModifier.modifierId);
            if (!modifier) continue;
            const oItemModifier: IOrderItemModifier = {
                CountForPricing: itemModifier.countForPricing,
                Index: itemModifier.index,
                IsDefault: itemModifier.isDefault,
                ModifierDisplayGroupId: itemModifier.modifierDisplayGroupId,
                ModifierId: itemModifier.modifierId,
                Name: JSON.stringify(modifier.Names),
                PreModifierId: itemModifier.preModifierId,
                CountForPricingByItself: itemModifier.countForPricingByItself,
                Price: 0,
                iModifierDisplayGroupIndex: itemModifier.iModifierDisplayGroupIndex,
                MergedModifierDisplayGroupId: itemModifier.mergedModifierDisplayGroupId,
                iIndex: itemModifier.iIndex,
                ModifierDisplayGroupIndex: itemModifier.modifierDisplayGroupIndex,
                IsCrust: modifier.IsCrust
            };
            orderItemModifiers.push(oItemModifier);
        }
        return orderItemModifiers;
    }

    private readonly _buildFavoriteStoresList = async (): Promise<void> => {
        Util.setElement("innerHTML", "div_favorite_locations_parent", "");

        const userFavStores = this._profileService.user.getProperty("FavoriteStores") || [];
        if (userFavStores.length > 0) {
            await this._determineUserLocationAndSortStores();
            const cards = await Promise.all(userFavStores.map(async (storeKey) => {
                const storeLocation = this._data.locations.find(store => store.storeKey === storeKey);
                return storeLocation ? this._createFavoriteStoreCard(storeLocation) : null;
            }));

            const containerDiv = document.createElement("div");
            containerDiv.classList.add("favorite-container");
            containerDiv.id = "favorite-stores-container";
            cards.filter(Boolean).forEach(card => containerDiv.appendChild(card!));

            const button = document.createElement("button");
            button.textContent = `${this._localizationService.names("Find")} More ${this._localizationService.names("Locations")}`;
            button.classList.add("no-fav-button", "secondary");
            button.onclick = () => {
                this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS);
            };
            containerDiv.appendChild(button);

            document.getElementById("div_favorite_locations_parent")?.appendChild(containerDiv);
        } else {
            this._renderNoFavLocation();
        }
    }

    private readonly _determineUserLocationAndSortStores = async (): Promise<void> => {
        const geoLocation = await Util.getCurrentPositionAsync();

        if (geoLocation) {
            const { latitude, longitude } = geoLocation;

            Util.setLocationCookie(latitude, longitude, 3600);
            this._sortStoresByProximity(latitude, longitude);
        } else {
            // Fallback: Use the location from the cookie
            const userLocation = Util.getUserLocationFromCookie();
            if (userLocation) {
                this._sortStoresByProximity(userLocation.lat, userLocation.lng);
            }
        }
    }

    private readonly _sortStoresByProximity = (userLat: number, userLng: number): void => {
        this._data.locations.sort((a, b) => {
            const distanceA = Util.calculateDistanceByLatLong(userLat, userLng, a.latitude, a.longitude);
            const distanceB = Util.calculateDistanceByLatLong(userLat, userLng, b.latitude, b.longitude);
            return distanceA - distanceB; // Sort in ascending order of distance
        });
    };

    private readonly _createFavoriteStoreCard = (store: IStoreLocation): HTMLElement => {
        const card = document.createElement("div");
        card.classList.add(`favorite-store-card`);
        card.id = `favorite-store-card-${store.storeKey}`;

        card.appendChild(this._createStoreInfo(store));
        card.appendChild(this._createStoreButtons(store));
        card.appendChild(this._createFavoriteStoreIcon(store));

        return card;
    };

    private readonly _createStoreInfo = (store: IStoreLocation): HTMLElement => {
        const storeInfo = document.createElement("div");
        storeInfo.classList.add("store-info");

        const storeName = document.createElement("h3");
        storeName.textContent = store.favName || store.name;
        storeName.classList.add("favorite-store-name");
        storeInfo.appendChild(storeName);

        const address = Util.formatAddressObject({
            StreetNo: store.streetNumber,
            Address1: store.address1,
            Address2: store.address2,
            Address3: store.address3,
            Address4: store.address4,
            Address5: store.address5,
            City: store.city,
            State: store.state,
            ZipCode: store.zip,
            CountryID: store.countryId || 1,
            AddressTypeID: 0
        }, this._data.getBrandProperty("Countries"));

        const storeAddress1 = document.createElement("p");
        storeAddress1.textContent = `${store.address1}`;
        storeAddress1.classList.add("favorite-store-address", "top");
        storeInfo.appendChild(storeAddress1);

        if (address.address3) {
            const storeAddress3 = document.createElement("p");
            storeAddress3.textContent = `${address.address3}`;
            storeAddress3.classList.add("favorite-store-address");
            storeInfo.appendChild(storeAddress3);
        }

        const storeCityState = document.createElement("p");
        storeCityState.textContent = `${address.cityState}`;
        storeCityState.classList.add("favorite-store-address", "bottom");
        storeInfo.appendChild(storeCityState);

        const storeHours = document.createElement("p");
        const hoursText = this._createHoursText(store.hours);
        storeHours.textContent = hoursText;
        storeHours.classList.add("favorite-store-hours");
        storeInfo.appendChild(storeHours);

        return storeInfo;
    };

    private readonly _createStoreButtons = (store: IStoreLocation): HTMLElement => {
        const buttonContainer = document.createElement("div");
        buttonContainer.classList.add("favorite-store-button-container");

        const orderTypes = store.orderTypes || [];
        orderTypes.sort((a, b) => a - b); // Ensure order types are sorted 123

        orderTypes.forEach(type => {
            const button = document.createElement("button");
            const names = this._data.getBrandProperty("OrderTypeSubTypes")?.find(x => x.OrderTypeId == type)?.Names;
            button.textContent = names ? this._localizationService.GetName(names) : "";

            // add ORDER text for fewer buttons
            if (orderTypes.length <= 2) {
                button.textContent = `ORDER ${button.textContent}`;
            }

            // Assign classes based on button count
            if (orderTypes.length === 1) {
                button.classList.add("favorite-store-order-button", "favorite-store-order-button-single");
            } else if (orderTypes.length === 2) {
                button.classList.add(
                    "favorite-store-order-button",
                    type === OrderType.DELIVERY
                        ? "favorite-store-order-button-delivery"
                        : "favorite-store-order-button-secondary",
                );
                if (type !== OrderType.DELIVERY)
                    button.classList.add("secondary");

            } else if (orderTypes.length === 3) {
                button.classList.add("favorite-store-order-button", "favorite-store-order-button-three");

                if (type !== OrderType.DELIVERY)
                    button.classList.add("secondary");
            }

            button.addEventListener("click", () => {
                this._routeToOrder(store.storeKey, type);
            });

            buttonContainer.appendChild(button);
        });

        return buttonContainer;
    };

    private readonly _createFavoriteStoreIcon = (store: IStoreLocation): HTMLElement => {
        const favoriteIconButton = document.createElement("button");
        favoriteIconButton.id = "favorite-store-icon";
        favoriteIconButton.setAttribute("aria-label", "remove favorite");
        favoriteIconButton.classList.add("icon-heart-filled", "favorites");
        favoriteIconButton.onclick = async () => {
            this._showConfirmRemoveFavoriteStoreDialog(store.storeKey);
        };

        return favoriteIconButton;
    };

    private readonly _showConfirmRemoveFavoriteStoreDialog = (storeKey: string): void => {
        new Dialog(
            this._localizationService.names("favoriteStoreDelete_Confirm"),
            [
                { label: this._localizationService.names("Cancel").toUpperCase(), value: "no", isSecondary: true },
                { label: this._localizationService.names("Remove").toUpperCase(), value: "yes", action: () => this._handleRemoveFavoriteStore(storeKey) }
            ]);
    };

    private readonly _handleRemoveFavoriteStore = async (storeKey: string): Promise<void> => {
        const loader = new LoaderSpinner();
        const checkDelete = await this._profileService.deleteFavoriteStore(storeKey);

        if (checkDelete)
            this._eventBus.emit(EventTypes.FAV_STORE_REMOVE, { storeKey, isFavoritesPage: true });
        else
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("favoriteStoreDelete_Error"),
                true
            );

        loader.destroy();

        if (this._dataLayer)
            this._dataLayer.remove_favorite(ContentType.LOCATION, this._data.getProperty("StoreInfo").storeCode.toString());
    }

    private readonly _clearFavoritesPage = () => {
        Util.setElement("innerHTML", "div_favorite_items_parent", "");
        Util.setElement("innerHTML", "div_favorite_locations_parent", "");
    }

    private readonly _buildFavoritesTabBar = (): ITabBarData => {
        const tabsDiv = document.getElementById("div_favorite_tabs");
        if (!tabsDiv)
            return { buttonId: "", tabs: [] };

        tabsDiv.innerHTML = "";

        const self = this;
        const tabData: ITabBarData = {
            buttonId: "btn_menu_favorite_tab",
            tabs:
                [
                    { id: -1, text: this._localizationService.names("MenuItems"), ltag: "MenuItems", function: () => self._onTabButtonClick(-1) },
                    { id: -2, text: this._localizationService.names("Locations"), ltag: "Locations", function: () => self._onTabButtonClick(-2) }
                ]
        };

        const tabBar = new TabBar(tabData, this._localizationService);
        tabsDiv.appendChild(tabBar.element);

        return tabData;
    }

    private readonly _onTabButtonClick = (tabId: number): void => {
        document.querySelectorAll(".menu-tab").forEach(tab => tab.classList.remove("active"));

        Util.showElement(tabId === -1 ? "div_favorite_items_parent" : "div_favorite_locations_parent");
        Util.hideElement(tabId === -1 ? "div_favorite_locations_parent" : "div_favorite_items_parent");

        const tabToActivate = document.getElementById(`btn_menu_favorite_tab_${tabId}`) || document.getElementById("btn_menu_favorite_tab_-1");
        tabToActivate?.classList.add("active");
    };

    private readonly _renderNoFavUI = (parentDivId: string, headingText: string, instructionText: string, buttonText: string, event: () => void): void => {
        const parentDiv = document.getElementById(parentDivId);
        if (!parentDiv) {
            return;
        }

        parentDiv.innerHTML = "";

        const noFavContainer = document.createElement("div");
        noFavContainer.classList.add("no-fav-container");

        const card = document.createElement("div");
        card.classList.add("no-fav-card");

        const heading = document.createElement("p");
        heading.textContent = headingText;
        heading.classList.add("no-fav-heading");
        card.appendChild(heading);

        const instructions = document.createElement("p");
        instructions.textContent = instructionText;
        instructions.classList.add("no-fav-instructions");
        card.appendChild(instructions);

        noFavContainer.appendChild(card);

        const button = document.createElement("button");
        button.textContent = buttonText;
        button.classList.add("no-fav-button", "secondary", "w-100");
        button.onclick = event;

        noFavContainer.appendChild(button);

        parentDiv.appendChild(noFavContainer);
    };

    private readonly _renderNotLoggedIn = (): void => {
        //2DO shared component for no login. For now just placeholders for MVP
        const tabDivIds = ["div_favorite_items_parent", "div_favorite_locations_parent"];
        for (const divId of tabDivIds) {
            this._renderNoFavUI(
                divId,
                this._localizationService.names("YouAreNotSignedIn").toUpperCase(),
                this._localizationService.names("SignInToSeeYourFavorites"),
                this._localizationService.names("SignIn").toUpperCase(),
                () => this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN)
            );
        }
    }

    private readonly _renderNoStoreSelected = (): void => {
        //2DO localize. need confirmation from BA on text
        this._renderNoFavUI(
            "div_favorite_items_parent",
            "YOU HAVEN'T SELECTED A STORE YET!",
            "Select a store to continue to favorites.",
            `${this._localizationService.names("Find")} ${this._localizationService.names("Locations")}`.toUpperCase(),
            () => this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS)
        );
    }

    private readonly _renderNoFavMenuItem = (): void => {
        this._renderNoFavUI(
            "div_favorite_items_parent",
            this._localizationService.names("FavoriteObjectNone").replaceAll("{{object}}", this._localizationService.names("MenuItems").toUpperCase()),
            this._localizationService.names("FavoriteObjectInstruction").replaceAll("{{object}}", this._localizationService.names("MenuItems").toLowerCase()),
            `Go to ${this._localizationService.names("Menu")}`.toUpperCase(),
            () => this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.MENU)
        );
    };

    private readonly _renderNoFavLocation = (): void => {
        this._renderNoFavUI(
            "div_favorite_locations_parent",
            this._localizationService.names("FavoriteObjectNone").replaceAll("{{object}}", this._localizationService.names("Locations").toUpperCase()),
            this._localizationService.names("FavoriteObjectInstruction").replaceAll("{{object}}", "stores"),
            `${this._localizationService.names("Find")} ${this._localizationService.names("Locations")}`.toUpperCase(),
            () => this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS)
        );
    };

    private readonly _routeToOrder = (storeKey: string, orderTypeId: OrderType): void => {
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.MENU);
        this._eventBus.emit(EventTypes.STORE_CHANGED, storeKey, this._profileService.loyaltyProvider);
        this._eventBus.emit(EventTypes.ORDER_TYPE_CHANGED, orderTypeId, this._profileService.loyaltyProvider);
    }

    /**
     * Creates a formatted text for store hours based on the current day and store hours.
     * @param {IStoreLocationHour[]} hours - The store's operating hours.
     * @returns {string} An object containing the localized hours text.
     */
    private readonly _createHoursText = (hours: IStoreLocationHour[]): string => {
        const currentDay = new Date().getDay();
        const todayHours = hours.find(h => h.weekdayId === currentDay);

        if (todayHours) {
            // Format the time range for the current day
            const languageCode = this._context.getProperty("languageCode");
            const startTime = Util.formatTimeByIsoCode(todayHours.startMinute, languageCode);
            const endTime = Util.formatTimeByIsoCode(todayHours.endMinute, languageCode);

            return `${this._localizationService.names("TodayHours")} ${startTime} - ${endTime}`;
        }

        // If no hours are found for today, return "Closed"
        return this._localizationService.names("Closed");
    };

}